import { serverTimestamp, Timestamp } from 'firebase/firestore';
import { isEmpty, isObject, map } from 'lodash';
import {
  toDeclaredServiceDelivery, toServiceDeliveryDocument,
} from 'src/contexts/treatmentPlanning';
import AppUtils from 'src/utils/AppUtils';

import AppointmentsDB from './appointments-db';
import GenericDB from './generic-db';

export default class SessionActivitiesDB extends GenericDB {
  constructor() {
    super('sessionActivities');
  }

  /**
   * Prepare a default sessionActivity object.
   *
   * @param {Object} session
   * @param {string} user
   * @param {string} userName
   * @param {string} activityType
   * @param {Timestamp} startTimestamp
   * @param {string | null} [sessionNoteTemplateId]
   * @param {string | null} [appointment]
   * @return {Promise<void>}
   */
  static prepareSessionActivityData(
    session,
    user,
    userName,
    activityType,
    startTimestamp,
    sessionNoteTemplateId = null,
    appointment = null,
  ) {
    return {
      client: session.client,
      clinic: session.clinic,
      session: session.id,
      sessionName: session.sessionName,
      type: activityType,
      user,
      userName,
      startTimestamp,
      endTimestamp: null,
      startGeolocation: null,
      endGeolocation: null,
      originalStartTimestamp: startTimestamp,
      originalEndTimestamp: null,
      sessionDurationOffset: session.duration || 0,
      sessionDurationEnd: null,
      status: 'in-progress',
      notes: '',
      sessionNoteTemplateId,
      appointment,
      createdBy: user,
      updateBy: user,
      createTimestamp: serverTimestamp(),
      isDeleted: false,
      ...(session.clientFile && { clientFile: session.clientFile }),
      ...SessionActivitiesDB.inheritedServiceDelivery(session, activityType),
    };
  }

  /**
   * The service delivery a session plan declared, carried onto the activity it produces.
   *
   * READ FROM THE FIELD THE ACTIVITY TYPE ANSWERS TO, WRITTEN AS `serviceDelivery`. The plan
   * qualifies its fields because it declares one per activity type it produces; the activity does
   * not, because it records the one it delivered. Which field that is belongs to the mapper, which
   * is the layer allowed to name the mud's fields.
   *
   * A WHOLE PAIR OR NOTHING. It round-trips through the value object rather than being spread field
   * by field, so an activity can never end up holding a place from one source and a service from
   * another — a pair neither of them authored. That also normalizes what the plan happens to store
   * and drops anything the model does not declare.
   *
   * Only onto a type that can carry one: a break or a drive records time without delivering a
   * service, and a delivery on one would assert something nobody attested to. A supervision activity
   * takes the supervision declaration, never the session's.
   *
   * Absent when the plan declared nothing for this type, rather than an empty delivery — that keeps
   * a plan with no declaration behaving exactly as it did before, with the activity editor creating
   * the empty one on first save.
   *
   * @param {Object} session
   * @param {string} activityType
   * @return {{serviceDelivery: Object}|{}}
   */
  static inheritedServiceDelivery(session, activityType) {
    const inherited = toServiceDeliveryDocument(toDeclaredServiceDelivery(session, activityType));

    return inherited ? { serviceDelivery: inherited } : {};
  }

  /**
   * For now used only for Offline Mode.
   *
   * @param {Object} session
   * @param {string} user
   * @param {string} userName
   * @param {string} activityType
   * @param {Timestamp} startTimestamp
   * @param {string | null} [sessionNoteTemplateId]
   * @param {string | null} [appointment]
   * @return {Promise<void>}
   */
  async createBySessionData(
    session,
    user,
    userName,
    activityType,
    startTimestamp,
    sessionNoteTemplateId = null,
    appointment = null,
  ) {
    const sessionActivity = SessionActivitiesDB.prepareSessionActivityData(
      session,
      user,
      userName,
      activityType,
      startTimestamp,
      sessionNoteTemplateId,
      appointment,
    );

    if (appointment) {
      const appointmentsDB = new AppointmentsDB();
      const appointmentData = await appointmentsDB.read(appointment);
      if (!appointmentData) {
        return this.create(sessionActivity);
      }

      sessionActivity.appointmentData = {
        integrationReference: appointmentData.integrationReference,
        integrationClientParRef: appointmentData.integrationClientParRef,
        integrationStaffParRef: appointmentData.integrationStaffParRef,
        integrationClientRef: appointmentData.integrationClientRef,
        integrationStaffRef: appointmentData.integrationStaffRef,
        integrationAppointmentType: appointmentData.integrationAppointmentType,
        integration: appointmentData.integration || null,
        integrationExternalUrl: appointmentData.integrationExternalUrl || null,
      };

      const sessionActivityDoc = await this.create(sessionActivity);

      if ((appointmentData.status === 'scheduled') && (appointmentData.user === user)) {
        await appointmentsDB.update(
          {
            id: appointment,
            session: session.id,
            sessionActivity: sessionActivityDoc.id,
            status: 'in-progress',
          },
        );
      }

      return;
    }

    return this.create(sessionActivity);
  }

  /**
   * For now used only for Offline Mode.
   *
   * @param {Object} session
   * @param {string} [activityType]
   * @param {boolean} [forceIndexedDB] force the use of IndexedDB
   * @return {Promise<void>}
   */
  async finishBySession(session, activityType, forceIndexedDB = false) {
    const sessionActivitiesConstraints = [
      ['client', '==', session.client],
      ['clinic', '==', session.clinic],
      ['session', '==', session.id],
      ['status', '==', 'in-progress'],
    ];
    if (activityType) {
      sessionActivitiesConstraints.push(['type', '==', activityType]);
    }

    const sessionActivities = await this.readAll(sessionActivitiesConstraints, null, null, null, null, {
      forceIndexedDB,
    });

    const sessionActivityActions = sessionActivities.map((sessionActivity) => {
      let status = 'finished';
      if (['break', 'transportation'].includes(sessionActivity.type)) {
        // Session notes are not required for breaks and transportation
        status = 'closed';
      }

      const actions = [];
      if (sessionActivity.appointment) {
        const appointmentsDb = new AppointmentsDB();
        actions.push(appointmentsDb.update(
          {
            id: sessionActivity.appointment,
            status: 'completed',
          },
          forceIndexedDB,
        ));
      }

      const now = Timestamp.now();
      const timestamp = session.finishTimestamp || now;

      actions.push(this.set(
        {
          status,
          endTimestamp: timestamp,
          originalEndTimestamp: timestamp,
          sessionDurationEnd: session.duration || 0,
          updateTimestamp: now,
        },
        sessionActivity.id,
        forceIndexedDB,
      ));

      return Promise.all(actions);
    });

    await AppUtils.allSettled(sessionActivityActions, (err) => {
      console.error('Error closing session activities: ', err);
    });
  }

  softDelete(sessionActivityId) {
    return this.update({
      id: sessionActivityId,
      isDeleted: true,
      deleteTimestamp: serverTimestamp(),
    });
  }

  /**
   * @param {Object & {id: string, client: string}} session
   */
  async softDeleteBySession(session) {
    if (!session || !isObject(session)) {
      return;
    }

    const activitiesRelatedToSession = await this.readAll([
      ['client', '==', session.client],
      ['session', '==', session.id],
    ]);

    if (isEmpty(activitiesRelatedToSession)) {
      return;
    }

    await AppUtils.allSettled(
      map(activitiesRelatedToSession, (sessionActivity) => this.softDelete(sessionActivity.id)),
    );
  }
}
